import { useCallback, useMemo, useState } from 'react';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { t } from 'i18next';
import { Icon } from '@frontend/icons';
import { allowedNotificationTypes } from '@frontend/settings-routing';
import { useGetTaskCenterCustomizationFlagValue } from '@frontend/task-center-scope';
import { usePreference } from '@frontend/user-preferences';
import { theme } from '@frontend/theme';
import { Button, Text } from '@frontend/design-system';
import { PaletteColorValues, PaletteColorType } from '../constants';

const ColorPalette = ({ selectedColor, onChange }: { selectedColor: string; onChange: (color: string) => void }) => {
  return (
    <div css={{ display: 'flex', gap: theme.spacing(1), marginTop: theme.spacing(0.5), alignItems: 'center' }}>
      {PaletteColorValues.map((palettleColor: PaletteColorType) => {
        const currentColor = selectedColor === palettleColor.color;
        return (
          <div
            key={palettleColor.textColor}
            style={
              currentColor ? { border: `2px solid ${theme.colors.primary50}`, padding: 2, borderRadius: '50%' } : {}
            }
            onClick={() => onChange(palettleColor.color)}
          >
            <div style={{ ...colorStyles, backgroundColor: palettleColor.textColor }}>
              {currentColor && <Icon height={12} width={12} name='check-small' color='white' />}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const ColorFilters = ({ closePopOver }: { closePopOver: () => void }) => {
  const TAGS = 'Tags';
  const { isTaskCenterEnabled } = useGetTaskCenterCustomizationFlagValue();
  const hideAnyFilters = [
    NotificationType.CHAT_MESSAGE,
    !isTaskCenterEnabled && NotificationType.TASK,
    NotificationType.CALL_INTELLIGENCE_UNHAPPY_CALL,
    NotificationType.AI_RECEPTIONIST_STATE,
  ];
  const options = useMemo(
    () => [
      ...allowedNotificationTypes
        .filter((notification) => !hideAnyFilters.includes(notification.firestoreType))
        .map((notification) => ({ value: notification.firestoreType, name: notification.displayName })),
      { value: TAGS as NotificationType, name: TAGS },
    ],
    []
  );

  const [forYouNotificationColors, setForYouNotificationColors] = usePreference('ForYouNotificationColors');

  const [localColors, setLocalColors] = useState<Record<string, string>>(forYouNotificationColors);

  const handleColorChange = (key: string, newColor: string) => {
    setLocalColors((prev) => ({
      ...prev,
      [key]: newColor,
    }));
  };

  const handleSave = useCallback(() => {
    setForYouNotificationColors(localColors);
    closePopOver();
  }, [localColors]);

  return (
    <div>
      {options.map((notification) => {
        return (
          <div
            key={notification.name}
            css={{ padding: theme.spacing(2), borderBottom: `1px solid ${theme.colors.neutral20}` }}
          >
            <Text css={{ fontWeight: theme.font.weight.bold }}>{notification.name}:</Text>
            <ColorPalette
              selectedColor={localColors[notification.value]}
              onChange={(newColor: string) => handleColorChange(notification.value, newColor)}
            />
          </div>
        );
      })}
      <div
        css={{
          padding: theme.spacing(2),
          zIndex: 1,
          position: 'sticky',
          bottom: '0',
          background: 'white',
          borderTop: `1px solid ${theme.colors.neutral20}`,
        }}
      >
        <Button
          onClick={handleSave}
          css={{ width: '100%' }}
          variant='primary'
          size='large'
          type='submit'
          trackingId='save-changes-colors-filters'
        >
          {t('Save changes')}
        </Button>
      </div>
    </div>
  );
};

const colorStyles = {
  height: '20px',
  width: '20px',
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  overflow: 'hidden',
};
